import type React from 'react';
import { useMemo } from 'react';
import { FormRenderer } from '@shadeables/ui';
import { BASE_FORM_CONFIG, type ShaderConstructor } from '@shadeables/shadermodel';
import { shaderModels } from '../../../../types';
import { useProjectContext } from '../../ProjectContext';
import styles from './FormSection.module.css';

export const FormSection: React.FC = () => {
  const { selectedShader, shaderModel, formValues, updateFormValues } =
    useProjectContext();

  // Memoize the shader model and form config to prevent unnecessary recalculations
  const CurrentShader = useMemo(
    () => (selectedShader ? shaderModels[selectedShader] : null),
    [selectedShader]
  );

  const formConfig = useMemo(() => {
    if (!CurrentShader) {
      return null;
    }

    // Type assertion is safe because all shader Model classes implement getFormConfig
    const shaderConfig = (CurrentShader.Model as ShaderConstructor).getFormConfig();
    // Combine base config with shader-specific config
    return [...BASE_FORM_CONFIG, ...shaderConfig];
  }, [CurrentShader]);

  if (!shaderModel || !selectedShader) {
    return (
      <div className={styles.container}>
        <div className={styles.skeletonForm}>
          <div className={styles.skeletonFieldGroup}>
            <div className={styles.skeletonLabel}></div>
            <div className={styles.skeletonInput}></div>
          </div>
          <div className={styles.skeletonFieldGroup}>
            <div className={styles.skeletonLabel}></div>
            <div className={styles.skeletonInput}></div>
          </div>
          <div className={styles.skeletonFieldGroup}>
            <div className={styles.skeletonLabel}></div>
            <div className={styles.skeletonSlider}></div>
          </div>
          <div className={styles.skeletonFieldGroup}>
            <div className={styles.skeletonLabel}></div>
            <div className={styles.skeletonColorPicker}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {formConfig ? (
        <FormRenderer
          config={formConfig}
          values={formValues}
          onChange={updateFormValues}
        />
      ) : (
        <div className={styles.fallback}>
          No configuration available for this shader.
        </div>
      )}
    </div>
  );
};
